import {
  NODE_DEFS,
  activeParams,
  activeRefs,
  evaluateGraph,
  evaluateNode,
  evaluateValue,
  isValueNode,
  parseRef,
  pickablePieces,
  refOptions,
  refPoint,
  trimEdges,
  nodeInputs,
  upstreamOf,
  valueSourceLabel,
  valueSources,
  type GraphNode,
  type NodeGraph,
  type NodeParamDef,
  type NodeRefDef,
} from '@lakaran/nodes';

/**
 * The shape of one node card, as data.
 *
 * The DOM editor builds cards as elements; the GPU editor needs the same information as plain
 * rows it can draw and hit-test. Keeping the layout in one place means both editors agree on what a
 * card says, and the GPU scene can rebuild a single card in O(rows) when only that card changed.
 */

export type CardRowKind =
  | 'ref'
  | 'param'
  | 'choice'
  | 'text'
  | 'value'
  | 'badge'
  | 'button'
  | 'list';

export interface CardRow {
  /** Field id the row edits (`ox`, `width`, the ref id, …). */
  id: string;
  kind: CardRowKind;
  label: string;
  /** Value as drawn on the card; the editor replaces it while focused. */
  text: string;
  /** True when a value step drives this number. */
  bound?: boolean;
  /** True when a reference owns this number (a read-out). */
  derived?: boolean;
  /** For `button` rows: which action it runs; `drop-exclude` puts one piece back. */
  action?: 'trim' | 'mirror' | 'clear-trim' | 'exclude' | 'drop-exclude' | 'restore-trim';
}

export interface CardModel {
  id: string;
  type: GraphNode['type'];
  title: string;
  /** Position in node-world units. */
  x: number;
  y: number;
  width: number;
  height: number;
  isOutput: boolean;
  isValue: boolean;
  selected: boolean;
  /** Value shown next to the title for value steps (`= 40`). */
  badge?: string;
  rows: CardRow[];
  /** Content signature: unchanged means the card does not need rebuilding. */
  signature: string;
}

export const CARD_WIDTH = 230;
export const CARD_HEADER = 28;
export const ROW_HEIGHT = 24;
export const CARD_PAD = 6;
/** Width of the insert rail docked on the left, so a view can keep the graph clear of it. */
export const RAIL_WIDTH = 156;

const round = (value: number): string =>
  String(Math.round(value * 1000) / 1000);

function paramText(graph: NodeGraph, node: GraphNode, param: NodeParamDef): {
  text: string;
  bound: boolean;
  derived: boolean;
} {
  const bound = Boolean(node.values?.[param.id]);
  if (bound) {
    return { text: round(evaluateValue(graph, node.values![param.id]!) ?? 0), bound: true, derived: false };
  }
  if (param.choices) {
    const raw = node.params[param.id] ?? param.value;
    const choice = param.choices.find((item) => item.value === Math.round(raw));
    return { text: choice?.label ?? round(raw), bound: false, derived: false };
  }
  const value = node.params[param.id] ?? param.value;
  // A picked reference owns X/Y, so those two show the snapped coordinate.
  const derived =
    (param.id === 'ox' && node.refs?.['reference']) || false ||
    (param.id === 'oy' && node.refs?.['reference']) || false ||
    ((param.id === 'x1' || param.id === 'y1') && node.refs?.['from']) ||
    ((param.id === 'x2' || param.id === 'y2') && node.refs?.['to']);
  return { text: round(value), bound: false, derived: Boolean(derived) };
}

function refText(graph: NodeGraph, node: GraphNode, def: NodeRefDef): string {
  const value = node.refs?.[def.id];
  if (!value) return '— none —';
  const match = refOptions(graph, node.id, (id) => evaluateNode(graph, id, '0'), def.kinds).find(
    (option) => option.value === value,
  );
  return match?.label ?? value;
}

/**
 * Everything one card draws, in the order the DOM card shows it: reference pickers, the text field,
 * the step-specific buttons, then the parameters and their `ƒ` bindings.
 */
export function buildCardModel(
  graph: NodeGraph,
  node: GraphNode,
  selected: boolean,
): CardModel {
  const def = NODE_DEFS[node.type];
  const rows: CardRow[] = [];

  // Declared input sockets first, so A and B read at the top of the card, on the input edge.
  for (const socket of nodeInputs(node)) {
    const source = upstreamOf(graph, node.id, socket.slot);
    rows.push({
      id: `in:${socket.slot}`,
      kind: 'badge',
      label: socket.label,
      text: source ? NODE_DEFS[source.type].title : '— none —',
    });
  }

  for (const refDef of activeRefs(node)) {
    rows.push({
      id: refDef.id,
      kind: 'ref',
      label: refDef.label,
      text: refText(graph, node, refDef),
    });
  }

  if (def.text) {
    rows.push({
      id: def.text.id,
      kind: 'text',
      label: def.text.label,
      text: (node.text ?? def.text.value).trim() || def.text.value,
    });
  }

  if (def.listsEdges) {
    const edges = trimEdges(graph, node.id, '0');
    const cut = edges.filter((edge) => !edge.kept);
    rows.push({
      id: 'trim',
      kind: 'badge',
      label: 'Trimmed',
      text: edges.length === 0 ? 'no edges yet' : `${cut.length} of ${edges.length} trimmed`,
    });
    // One row per trimmed span, so hovering it can light exactly that span in the drawing.
    for (const edge of cut.slice(0, 6)) {
      rows.push({
        id: `trim:${edge.key}`,
        kind: 'button',
        label: '',
        text: `× ${edge.label}`,
        action: 'restore-trim',
      });
    }
    if (cut.length > 0) {
      rows.push({ id: 'clear-trim', kind: 'button', label: '', text: 'restore all', action: 'clear-trim' });
    }
    rows.push({ id: 'trim', kind: 'button', label: '', text: 'Trim in drawing', action: 'trim' });
  }

  if (def.listsPicks) {
    const pieces = pickablePieces(graph, node.id, '0');
    const picked = pieces.filter((piece) => piece.picked);
    const manual = Math.round(node.params['source'] ?? 0) >= 1;
    rows.push({
      id: 'picks',
      kind: 'badge',
      label: manual ? 'Picked' : 'Reflect',
      text: manual ? `${picked.length} of ${pieces.length} picked` : `all ${pieces.length} · previous shape`,
    });
    for (const piece of picked.slice(0, 5)) {
      rows.push({ id: `pick:${piece.key}`, kind: 'list', label: piece.label, text: '×' });
    }
    if (manual) {
      rows.push({ id: 'mirror', kind: 'button', label: '', text: 'Mirror in drawing', action: 'mirror' });
    }
  }

  if (def.listsExcludes) {
    const pieces = pickablePieces(graph, node.id, '0');
    const excluded = pieces.filter((piece) => piece.picked);
    rows.push({
      id: 'excludes',
      kind: 'badge',
      label: 'Left out',
      text:
        pieces.length === 0
          ? 'nothing wired in yet'
          : excluded.length === 0
            ? `all ${pieces.length} take part`
            : `${excluded.length} of ${pieces.length} pass through`,
    });
    for (const piece of excluded.slice(0, 5)) {
      rows.push({
        id: `exclude:${piece.key}`,
        kind: 'button',
        label: '',
        text: `× ${piece.label}`,
        action: 'drop-exclude',
      });
    }
    rows.push({
      id: 'exclude',
      kind: 'button',
      label: '',
      text: 'Exclude in drawing',
      action: 'exclude',
    });
  }

  for (const param of activeParams(node)) {
    const state = paramText(graph, node, param);
    rows.push({
      id: param.id,
      kind: param.choices ? 'choice' : 'param',
      label: param.label,
      text: state.text,
      bound: state.bound,
      derived: state.derived,
    });
  }

  const height = CARD_HEADER + CARD_PAD * 2 + rows.length * ROW_HEIGHT;
  const badge = isValueNode(node) ? `= ${round(evaluateValue(graph, node.id) ?? 0)}` : undefined;

  return {
    id: node.id,
    type: node.type,
    title: def.title,
    x: node.x,
    y: node.y,
    width: CARD_WIDTH,
    height,
    isOutput: node.type === 'emit',
    isValue: isValueNode(node),
    selected,
    badge,
    rows,
    signature: cardSignature(graph, node),
  };
}

/**
 * What the card draws, as a comparable string: the step's own fields, the points it is pinned to,
 * the numbers driving it, and — for the steps that list pieces — the stream it was handed. Anything
 * outside this is invisible on the card and must not force a rebuild.
 */
export function cardSignature(graph: NodeGraph, node: GraphNode): string {
  const parts: string[] = [
    node.type,
    JSON.stringify(node.params ?? {}),
    JSON.stringify(node.values ?? {}),
    JSON.stringify(node.picks ?? []),
    JSON.stringify(node.hidden ?? []),
    node.text ?? '',
  ];
  for (const [field, value] of Object.entries(node.refs ?? {})) {
    const ref = parseRef(value);
    const point = ref ? refPoint(graph, ref, (target) => evaluateNode(graph, target.id, '0')) : null;
    parts.push(
      `ref:${field}:${value}:${point ? `${point.x.toFixed(3)},${point.y.toFixed(3)}` : '-'}`,
    );
  }
  for (const [field, source] of Object.entries(node.values ?? {})) {
    parts.push(`fx:${field}:${valueSourceLabel(graph, source)}`);
  }
  const def = NODE_DEFS[node.type];
  if (def.listsEdges || def.listsPicks) {
    const upstream = upstreamOf(graph, node.id);
    const stream = upstream ? evaluateNode(graph, upstream.id, '0') : [];
    parts.push(`stream:${stream.map((entity) => entity.name ?? entity.id).join(',')}`);
  }
  if (isValueNode(node)) parts.push(`value:${evaluateValue(graph, node.id) ?? 'null'}`);
  // Sources available to the ƒ picker decide whether the row is editable at all.
  parts.push(`sources:${valueSources(graph, node.id).length}`);
  parts.push(`out:${evaluateGraph(graph, '0').length}`);
  return parts.join('|');
}

